import { supabase } from '../lib/supabase';

const REQUIRED_COLUMNS = ['institute_name', 'city', 'course_name', 'category', 'course_type', 'level', 'duration'];

const COLUMN_ALIASES = {
  'institute name': 'institute_name',
  'institutename': 'institute_name',
  'institute': 'institute_name',
  'college name': 'institute_name',
  'college': 'institute_name',
  'university': 'institute_name',
  'location': 'city',
  'campus city': 'city',
  'campus': 'city',
  'course name': 'course_name',
  'coursename': 'course_name',
  'name': 'course_name',
  'course type': 'course_type',
  'coursetype': 'course_type',
  'type': 'course_type',
  'specialization': 'specialization',
  'specialisation': 'specialization',
  'spec': 'specialization',
  'fees': 'fees',
  'fee': 'fees',
  'tuition fee': 'fees',
  'show fee': 'show_fee',
  'showfee': 'show_fee',
  'display fee': 'show_fee',
  'eligibility': 'eligibility',
  'admission process': 'admission_process',
  'admissionprocess': 'admission_process',
  'admission': 'admission_process',
  'placement stats': 'placement_stats',
  'placementstats': 'placement_stats',
  'placement': 'placement_stats',
  'accreditation': 'accreditation',
};

function normalizeHeader(raw) {
  const lower = raw?.trim()?.toLowerCase()?.replace(/\s+/g, ' ');
  return COLUMN_ALIASES?.[lower] || lower?.replace(/\s+/g, '_');
}

/**
 * Parse a CSV file and return rows with normalized headers
 * @param {File} file
 * @returns {Promise<object[]>}
 */
export function parseCsvFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e?.target?.result;
        const lines = text?.split(/\r?\n/)?.filter((l) => l?.trim());
        if (lines?.length < 2) {
          reject(new Error('CSV file must have a header row and at least one data row'));
          return;
        }

        // Parse header
        const rawHeaders = parseCSVLine(lines[0]);
        const headers = rawHeaders?.map(normalizeHeader);

        // Parse data rows
        let rows = [];
        for (let i = 1; i < lines?.length; i++) {
          const values = parseCSVLine(lines[i]);
          if (values?.every((v) => !v?.trim())) continue; // skip blank rows
          const row = {};
          headers?.forEach((h, idx) => {
            row[h] = values?.[idx] ?? '';
          });
          row._rowNumber = i + 1; // 1-indexed, row 1 = header
          rows?.push(row);
        }

        resolve(rows);
      } catch (err) {
        reject(new Error(`Failed to parse CSV: ${err?.message}`));
      }
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file, 'UTF-8');
  });
}

/**
 * Simple CSV line parser that handles quoted fields
 */
function parseCSVLine(line) {
  const result = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line?.length; i++) {
    const char = line?.[i];
    if (char === '"') {
      if (inQuotes && line?.[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      result?.push(current?.trim());
      current = '';
    } else {
      current += char;
    }
  }
  result?.push(current?.trim());
  return result;
}

/**
 * Validate all rows
 */
function validateRows(rows, validCategories, validLevels) {
  const errors = [];
  const validRows = [];

  if (!rows || rows?.length === 0) {
    errors?.push({ row: null, column: null, value: null, message: 'No data rows found in CSV' });
    return { errors, validRows };
  }

  const headers = Object.keys(rows?.[0] || {})?.filter((h) => h !== '_rowNumber');
  const missingHeaders = REQUIRED_COLUMNS?.filter((f) => !headers?.includes(f));
  if (missingHeaders?.length > 0) {
    errors?.push({
      row: null,
      column: null,
      value: null,
      message: `Missing required columns: ${missingHeaders?.join(', ')}. Please download the sample template.`,
    });
    return { errors, validRows };
  }

  rows?.forEach((row) => {
    const rowNumber = row?._rowNumber;
    const rowErrors = [];

    // Required fields
    REQUIRED_COLUMNS?.forEach((field) => {
      const val = String(row?.[field] ?? '')?.trim();
      if (!val) {
        rowErrors?.push({
          row: rowNumber,
          column: field,
          value: '',
          message: `"${field?.replace(/_/g, ' ')}" is required but empty`,
        });
      }
    });

    // Validate category
    const rawCategory = String(row?.category ?? '')?.trim();
    if (rawCategory && validCategories?.length > 0) {
      if (!validCategories?.includes(rawCategory?.toLowerCase())) {
        rowErrors?.push({
          row: rowNumber,
          column: 'category',
          value: rawCategory,
          message: `"${rawCategory}" is not a valid category`,
        });
      }
    }

    // Validate level
    const rawLevel = String(row?.level ?? '')?.trim();
    if (rawLevel && validLevels?.length > 0) {
      if (!validLevels?.includes(rawLevel?.toLowerCase())) {
        rowErrors?.push({
          row: rowNumber,
          column: 'level',
          value: rawLevel,
          message: `"${rawLevel}" is not a valid level`,
        });
      }
    }

    // Validate fees
    const rawFees = String(row?.fees ?? '')?.trim();
    if (rawFees) {
      const feeNum = parseFloat(rawFees);
      if (isNaN(feeNum) || feeNum < 0) {
        rowErrors?.push({
          row: rowNumber,
          column: 'fees',
          value: rawFees,
          message: `"${rawFees}" is not a valid fee amount`,
        });
      }
    }

    if (rowErrors?.length > 0) {
      errors?.push(...rowErrors);
    } else {
      validRows?.push(row);
    }
  });

  return { errors, validRows };
}

/**
 * Look up institute by name + city (case-insensitive)
 * city is stored on the users table, not institute_profiles
 */
async function findInstituteByNameAndCity(name, city) {
  // Step 1: fetch all institutes matching the name
  const { data: institutes, error: instError } = await supabase
    ?.from('institute_profiles')
    ?.select('id, institute_name, user_id')
    ?.ilike('institute_name', name?.trim())
    ?.limit(50);

  if (instError) throw instError;
  if (!institutes || institutes?.length === 0) return null;

  // Step 2: fetch city from users table for each matched institute
  const userIds = institutes?.map((i) => i?.user_id)?.filter(Boolean);
  if (userIds?.length === 0) return null;

  const { data: users, error: userError } = await supabase
    ?.from('users')
    ?.select('id, city')
    ?.in('id', userIds);

  if (userError) throw userError;

  const cityLower = city?.trim()?.toLowerCase();
  const userCityMap = {};
  (users || [])?.forEach((u) => {
    userCityMap[u?.id] = u?.city?.toLowerCase()?.trim() ?? '';
  });

  const match = institutes?.find(
    (inst) => userCityMap?.[inst?.user_id] === cityLower
  );

  return match || null;
}

/**
 * Insert a single course row
 */
async function insertCourse(instituteId, row) {
  const showFeeRaw = String(row?.show_fee ?? '')?.trim()?.toLowerCase();
  const showFee = !['false', '0', 'no']?.includes(showFeeRaw);

  const payload = {
    institute_id: instituteId,
    course_name: String(row?.course_name ?? '')?.trim(),
    category: String(row?.category ?? '')?.trim()?.toLowerCase(),
    course_type: String(row?.course_type ?? '')?.trim(),
    specialization: String(row?.specialization ?? '')?.trim() || null,
    level: String(row?.level ?? '')?.trim()?.toLowerCase(),
    duration: String(row?.duration ?? '')?.trim(),
    fees: row?.fees ? parseFloat(String(row?.fees)?.trim()) : null,
    show_fee: showFee,
    eligibility: String(row?.eligibility ?? '')?.trim() || null,
    admission_process: String(row?.admission_process ?? '')?.trim() || null,
    placement_stats: String(row?.placement_stats ?? '')?.trim() || null,
    accreditation: String(row?.accreditation ?? '')?.trim() || null,
  };

  const { error } = await supabase?.from('courses')?.insert(payload);
  if (error) throw error;
}

/**
 * Main upload function — processes CSV with institute_name + city matching
 * @param {File} file
 * @param {{ onProgress?: Function }} options
 */
export async function bulkCsvCourseUpload(file, { onProgress } = {}) {
  const allErrors = [];
  const summary = {
    totalRows: 0,
    totalRowsUploaded: 0,
    totalRowsFailed: 0,
    totalRowsSkipped: 0,
    instituteResults: [],
  };

  // 1. Fetch valid catalog values
  let validCategories = [];
  let validLevels = [];
  try {
    const { data } = await supabase?.from('global_course_catalog')?.select('name, type')?.eq('is_active', true);
    validCategories = (data || [])?.filter((d) => d?.type === 'category')?.map((d) => d?.name?.toLowerCase()?.trim());
    validLevels = (data || [])?.filter((d) => d?.type === 'level')?.map((d) => d?.name?.toLowerCase()?.trim());
  } catch (_) {
    // proceed without catalog validation
  }

  // 2. Parse CSV
  let rows;
  try {
    onProgress?.({ phase: 'parsing', rowIndex: 0, totalRows: 0 });
    rows = await parseCsvFile(file);
  } catch (err) {
    return { success: false, fatalError: err?.message, summary, errors: [] };
  }

  summary.totalRows = rows?.length;

  if (rows?.length === 0) {
    return { success: false, fatalError: 'No data rows found in CSV file.', summary, errors: [] };
  }

  // 3. Validate all rows
  onProgress?.({ phase: 'validating', rowIndex: 0, totalRows: rows?.length });
  const { errors: validationErrors, validRows } = validateRows(rows, validCategories, validLevels);

  if (validationErrors?.some((e) => e?.row === null)) {
    // Fatal structural error (missing columns)
    return { success: false, fatalError: validationErrors?.[0]?.message, summary, errors: validationErrors };
  }

  allErrors?.push(...validationErrors);
  summary.totalRowsFailed += validationErrors?.length;

  // 4. Group valid rows by institute_name + city
  const instituteGroups = new Map();
  validRows?.forEach((row) => {
    const key = `${String(row?.institute_name ?? '')?.trim()?.toLowerCase()}|||${String(row?.city ?? '')?.trim()?.toLowerCase()}`;
    if (!instituteGroups?.has(key)) {
      instituteGroups?.set(key, {
        institute_name: String(row?.institute_name ?? '')?.trim(),
        city: String(row?.city ?? '')?.trim(),
        rows: [],
      });
    }
    instituteGroups?.get(key)?.rows?.push(row);
  });

  // 5. Process each institute group
  const groups = Array.from(instituteGroups?.values());
  let processedRowCount = 0;

  for (let gi = 0; gi < groups?.length; gi++) {
    const group = groups?.[gi];
    const { institute_name, city, rows: groupRows } = group;

    onProgress?.({
      phase: 'uploading',
      instituteName: institute_name,
      city,
      groupIndex: gi + 1,
      totalGroups: groups?.length,
      rowIndex: processedRowCount,
      totalRows: validRows?.length,
    });

    // Look up institute
    let institute = null;
    try {
      institute = await findInstituteByNameAndCity(institute_name, city);
    } catch (err) {
      const msg = `Database error looking up "${institute_name}, ${city}": ${err?.message}`;
      groupRows?.forEach((row) => {
        allErrors?.push({ row: row?._rowNumber, column: 'institute_name', value: institute_name, message: msg });
      });
      summary.totalRowsSkipped += groupRows?.length;
      summary?.instituteResults?.push({
        institute_name,
        city,
        status: 'skipped',
        reason: 'Database lookup error',
        uploaded: 0,
        failed: groupRows?.length,
        total: groupRows?.length,
      });
      continue;
    }

    if (!institute) {
      const msg = `No institute found matching name "${institute_name}" in city "${city}". Check spelling and city name.`;
      groupRows?.forEach((row) => {
        allErrors?.push({ row: row?._rowNumber, column: 'institute_name', value: institute_name, message: msg });
      });
      summary.totalRowsSkipped += groupRows?.length;
      summary?.instituteResults?.push({
        institute_name,
        city,
        status: 'not_found',
        reason: 'Institute not found',
        uploaded: 0,
        failed: groupRows?.length,
        total: groupRows?.length,
      });
      continue;
    }

    // Upload rows for this institute
    let uploadedCount = 0;
    let failedCount = 0;

    for (let ri = 0; ri < groupRows?.length; ri++) {
      const row = groupRows?.[ri];
      processedRowCount++;

      onProgress?.({
        phase: 'uploading',
        instituteName: institute_name,
        city,
        groupIndex: gi + 1,
        totalGroups: groups?.length,
        rowIndex: processedRowCount,
        totalRows: validRows?.length,
      });

      try {
        await insertCourse(institute?.id, row);
        uploadedCount++;
        summary.totalRowsUploaded++;
      } catch (err) {
        const isDuplicate = err?.message?.toLowerCase()?.includes('duplicate') || err?.message?.toLowerCase()?.includes('unique');
        allErrors?.push({
          row: row?._rowNumber,
          column: 'course_name',
          value: row?.course_name,
          message: isDuplicate
            ? `Course "${row?.course_name}" already exists for this institute`
            : `Upload failed: ${err?.message}`,
        });
        failedCount++;
        summary.totalRowsFailed++;
      }
    }

    summary?.instituteResults?.push({
      institute_name,
      city,
      matched_name: institute?.institute_name,
      status: uploadedCount > 0 ? (failedCount > 0 ? 'partial' : 'success') : 'failed',
      uploaded: uploadedCount,
      failed: failedCount,
      total: groupRows?.length,
    });
  }

  return {
    success: summary?.totalRowsUploaded > 0,
    summary,
    errors: allErrors,
  };
}

/**
 * Download a sample CSV template for Option 4A bulk upload
 */
export function downloadSampleCsvTemplate() {
  const headers = [
    'institute_name',
    'city',
    'course_name',
    'category',
    'course_type',
    'specialization',
    'level',
    'duration',
    'fees',
    'show_fee',
    'eligibility',
    'admission_process',
    'placement_stats',
    'accreditation',
  ];

  const sampleRows = [
    [
      'Amrita University', 'Chennai',
      'Bachelor of Technology in Computer Science', 'engineering', 'Full Time',
      'Computer Science', 'undergraduate', '4 Years', '500000', 'true',
      '10+2 with PCM, Min 60%', 'JEE Main / AEEE', 'Avg 8 LPA, Top 12 LPA', 'AICTE, NBA',
    ],
    [
      'Amrita University', 'Coimbatore',
      'Master of Business Administration', 'business & management', 'Full Time',
      'Finance', 'post graduate', '2 Years', '800000', 'true',
      "Bachelor\'s Degree with 50%", 'CAT / MAT / XAT', 'Avg 12 LPA', 'AICTE, AACSB',
    ],
    [
      'Amrita University', 'Kochi',
      'Bachelor of Science in Nursing', 'medical & health sciences', 'Full Time',
      '', 'undergraduate', '4 Years', '350000', 'true',
      '10+2 with Biology, Min 50%', 'Direct Admission', 'Avg 4 LPA', 'INC, COA',
    ],
    [
      'Anna University', 'Chennai',
      'Bachelor of Engineering in Electronics', 'engineering', 'Full Time',
      'VLSI Design', 'undergraduate', '4 Years', '250000', 'true',
      '10+2 with PCM, Min 55%', 'TNEA Counselling', 'Avg 6 LPA', 'AICTE, NBA',
    ],
    [
      'Anna University', 'Chennai',
      'Master of Technology in Data Science', 'engineering', 'Full Time',
      'Machine Learning', 'post graduate', '2 Years', '180000', 'true',
      "B.E/B.Tech with 60%", 'TANCET / GATE', 'Avg 10 LPA', 'AICTE',
    ],
  ];

  // Build CSV content
  const escapeField = (val) => {
    const str = String(val ?? '');
    if (str?.includes(',') || str?.includes('"') || str?.includes('\n')) {
      return `"${str?.replace(/"/g, '""')}"`;
    }
    return str;
  };

  const csvLines = [
    headers?.join(','),
    ...sampleRows?.map((row) => row?.map(escapeField)?.join(',')),
  ];

  const csvContent = csvLines?.join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL?.createObjectURL(blob);
  const link = document?.createElement('a');
  link.href = url;
  link.download = 'bulk_course_upload_sample.csv';
  document?.body?.appendChild(link);
  link?.click();
  document?.body?.removeChild(link);
  URL?.revokeObjectURL(url);
}

/**
 * Download error report as CSV
 */
export function downloadCsvErrorReport(errors, summary) {
  const escapeField = (val) => {
    const str = String(val ?? '');
    if (str?.includes(',') || str?.includes('"') || str?.includes('\n')) {
      return `"${str?.replace(/"/g, '""')}"`;
    }
    return str;
  };

  const headers = ['Row', 'Column', 'Cell Value', 'Error Message'];
  let rows = errors?.map((e) => [
    e?.row ?? 'N/A',
    e?.column || 'N/A',
    e?.value ?? '',
    e?.message || '',
  ]);

  const csvLines = [headers?.join(','), ...rows?.map((r) => r?.map(escapeField)?.join(','))];
  const csvContent = csvLines?.join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL?.createObjectURL(blob);
  const link = document?.createElement('a');
  link.href = url;
  link.download = 'bulk_upload_errors.csv';
  document?.body?.appendChild(link);
  link?.click();
  document?.body?.removeChild(link);
  URL?.revokeObjectURL(url);
}
